import { ReactElement, useEffect } from 'react';
import App, { AppProps, AppContext } from 'next/app';
import router, { useRouter } from 'next/router';
import dynamic from 'next/dynamic';
import { I18nextProvider } from 'react-i18next';
import { Spin } from 'antd';
import { setCookie, delCookie } from 'ava-util/dist/cookie';
import { generatePath } from 'ava-util/dist/request';
import { hasClass, addClass } from 'ava-util/dist/dom-class';
import { getParamInSearch } from 'ava-util/dist/request';
import 'app/styles/globals.less';
import { getStore, getStoreContext, useLocalObservable } from 'base/store';
import { StorePropsType, initLoadStore, initComponentStore } from 'base/store/initialize';
import { parseAppContext } from 'base/utils/parse-app-context';
import { storage } from 'base/utils/localStorage';
import { appendPageCss, getBodyClass } from 'base/utils/global-dom';
import { ISeo } from 'base/interfacesV2/seo';
import { getSeoConfig } from 'base/servicesV2/seo';
import PageSeo from '@base/lib/PageSeo';
import { clearTrack, loadCtr } from 'base/utils/tracker';
import BtnDebug from '@base/lib/BtnDebug';
import ErrorDisplay from '@base/lib/ErrorDisplay';
import { initAppBridge } from 'base/utils/bridge';
import { getPageLayout, getUrlLocale, transLocale } from 'base/utils/request';
import { getNativePage, initRegisterAppHandler, nativeLogin } from 'base/utils/app';
import {
    COOKIE_ACCESS_TOKEN,
    COOKIE_LOGIN_TOKEN,
    COOKIE_LANG,
    COOKIE_CURRENCY,
    COOKIE_COUNTRY,
    QUERY_DISABLE_TRACKER,
    STORAGE_DISABLE_TRACKER,
    COOKIE_LAST_ORDER_ID,
    COOKIE_CART_SELECTED_IDS,
    COOKIE_VARIANT_NUMS_ITEMS,
    COOKIE_DEBUG_TRANS,
    COOKIE_BUY_NOW_DATA,
    COOKIE_LOCALE, WHOLESALE_NO_LOGIN_PAGES,
} from 'base/config/constant';
import { changeI18nLang, i18nextInstance, initI18next } from 'base/utils/i18n';
import { IconLoading } from '@base/icon';
import { QUERY_REFERRAL_CODE } from 'mod/referral/constant';
import { storeReferralCode } from 'mod/referral/utils';
import { checkAgeGate, getAgeGatePath } from 'mod/age-gate/utils';
import { pages } from 'pro/config/pages';
import { RouteEnum } from 'pro/config/routers';
import site from 'pro/config/site';
import { theme } from 'pro/config/theme';
import BaseLayout from '@app/common/BaseLayout';
import { GoogleConsent } from '@app/common/GoogleConsent';
import RiskifiedBeacon from '@base/snippets/RiskifiedBeacon';
import { isPhone } from 'base/utils/client';
import VConsole from '@base/snippets/VConsole';
import { GaInstaller, GtmInstaller, SnowplowInstaller } from '@base/snippets/TrackInstaller';
import { RemInstaller } from '@base/snippets/Rem';
import { GlobalMetas } from '@base/common/GlobalMetas';
import { getParamsInSearch, getSearch } from 'ava-util/dist/request';
import { getValidLocale } from 'mod/store/locale/utils';
import { isStringIncludedArrayString } from 'base/utils/string';
import { clearOldCookies } from 'base/utils/clearOldCookies';

const AppDebug = dynamic(() => import('@base/common/AppDebug'), { ssr: false });
const AosInstaller = dynamic(() => import('@base/common/AosInstaller'), { ssr: false });

declare const window: any;

type MyAppProps = AppProps & {
    Component: Globals.NextPage;
    storeProps: StorePropsType;
    seoProps: ISeo;
    pageProps: Record<string, any>;
}

const PopupAageGateBrands = ['Vivosun']
const isPopupAageGateBrands = PopupAageGateBrands.includes(site.brand)

//修改 antd loading 图标
Spin.setDefaultIndicator(<IconLoading/>);

function MyAppMain(props: MyAppProps): ReactElement {
    const { Component, pageProps, storeProps, seoProps } = props;
    const router = useRouter();

    const isFirstClientLoad = typeof getStore().rootStore === 'undefined' && typeof window !== 'undefined';
    const store = initComponentStore(storeProps); //更新非组件中获取的store数据
    const observableStore = useLocalObservable(() => store);

    const { rootStore, userStore, miniCartStore, persistStore } = observableStore;
    rootStore.updateAfterRouterChange(storeProps.rootState);
    persistStore.update(storeProps.persistState);
    const rootState = rootStore.state;

    initI18next(rootState.lang, persistStore?.state?.translation);

    if (typeof window !== 'undefined') {
        // 清理旧 cookie，旧 Cookie 有效期为 365 天, 上线一年后可删除这段代码
        clearOldCookies();
        // cookie中 保存当前语言，货币，国家，token
        const locale = `${rootState.lang}-${rootState.country}`;
        setCookie(COOKIE_LOCALE, locale === 'en-US' ? '' : locale);
        setCookie(COOKIE_LANG, rootState.lang);
        setCookie(COOKIE_CURRENCY, rootState.currency);
        setCookie(COOKIE_COUNTRY, rootState.country);
        setCookie(COOKIE_ACCESS_TOKEN, rootState.accessToken);
        setCookie(COOKIE_LOGIN_TOKEN, rootState.loginToken);
        if (rootState.debugTrans === '0') {
            delCookie(COOKIE_DEBUG_TRANS);
        } else {
            setCookie(COOKIE_DEBUG_TRANS, rootState.debugTrans);
        }
        
        // 更新 body 标签属性
        const body = document.body;
        // h5导航菜单在页面跳转过程中打开需要保留禁止滚动的style
        if (!hasClass(body, 'js-keep-style')) {
            body.removeAttribute('style');
        }
        body.className = getBodyClass(storeProps.rootState);
    }

    if (isFirstClientLoad) {
        // Tracker
        if (rootState.query[QUERY_DISABLE_TRACKER] === '1') {
            storage.set(STORAGE_DISABLE_TRACKER, '1');
        }
        //app
        initAppBridge();

        // 注册app 方法
        initRegisterAppHandler(rootState.query);

        if (typeof rootState.query[QUERY_REFERRAL_CODE] === 'string') {
            storeReferralCode(rootState.query[QUERY_REFERRAL_CODE]);
        }
    }

    useEffect(() => {
        const handleStart = (url: string, { shallow }: { shallow: boolean }) => {
            if (!shallow) {
                const urlLocale = getUrlLocale(url);
                if (urlLocale) {
                    const localeObj = transLocale(urlLocale);
                    if (localeObj.country !== rootState.country || localeObj.lang !== rootState.lang) {
                        const locale = getValidLocale(persistStore?.state?.localeList || [], {
                            country: localeObj.country || rootState.country,
                            lang: localeObj.lang || rootState.lang,
                            currency: rootState.currency,
                        });
                        setCookie(COOKIE_LOCALE, `${locale.lang}-${locale.country}`);
                        setCookie(COOKIE_LANG, locale.lang);
                        setCookie(COOKIE_CURRENCY, locale.currency);
                        setCookie(COOKIE_COUNTRY, locale.country);

                        if (locale.lang !== rootState.lang) {
                            changeI18nLang(locale.lang);
                        }
                    }
                }

                if (isPhone) {
                    const query = getParamsInSearch(getSearch(url));
                    if (query.country || query.currency) {
                        router.replace(generatePath(url, { country: void 0, currency: void 0 }));
                        throw 'Abort';
                    }
                }

                // 拦截 app 中router.push 触发的原生页面跳转
                if (rootState.ua.isApp) {
                    const pathname = url.replace(/[?#].*$/, '');
                    const nativePage = getNativePage(pathname);
                    if (nativePage) {
                        nativePage.callback(pathname, url);
                        // throw new Error('Abort');
                        throw "Abort route change by user's confirmation.";
                    }
                }

                window.scrollTo(0, 0);
                //解决页面跳转过程中页面滚动引起的h5页头分类菜单因为页面高度变化导致的scroll位置错误
                document.body.style.paddingBottom = '0px';
                addClass(document.body, 'body-loading', false);
                rootStore.update({
                    pageLoading: true,
                    pageLayout: getPageLayout(url),
                });
                miniCartStore.setVisible(false);

                //搜索页输入框内容与搜索词一致，非搜索页面，头部搜索输入框清空，目前只用于pc
                const searchInput = document.getElementById('js-search-input') as HTMLInputElement;
                const searchClear = document.getElementById('js-search-clear') as HTMLInputElement;
                if (searchInput) {
                    const q = url.startsWith(RouteEnum.SEARCH)
                        ? getParamInSearch('q', url.replace(/^.*?\?(.*)$/, '?$1')) || ''
                        : '';
                    searchInput.value = q;
                    if (searchClear && q) {
                        addClass(searchClear, 'g-active', false);
                    }
                }
            }
        };

        const handleStop = (url: string, { shallow }: { shallow: boolean }) => {
            if (rootState.envName !== 'prod' && rootState.ua.isApp) {
                // app打印调试
                console.log("routeChangeComplete ==", JSON.stringify({
                    location: window?.location || {},
                }, null, 1));
            }

            if (!shallow) {
                // 获取当前用户信息
                userStore.fetchUserInfo().then(res => {
                    if (rootState.ua.isApp) {
                        miniCartStore.setTotalQty(res.cartNum || 0);
                    }
                });

                if (!rootStore.state.delayPageLoading) {
                    rootStore.update({ pageLoading: false });
                }

                // 开发模式下页面跳转后无法加载页面 CSS
                appendPageCss(router.pathname);

                clearTrack();
                setTimeout(() => {
                    loadCtr();
                }, 50);
            }
        };

        const handleError = (err, url: string, { shallow }: { shallow: boolean }) => {
            console.log('routeChangeError', url, err);
            // if (!err.cancelled) {
            //     console.error(`[${url}]`, err)
            // }
        };

        if (rootStore.state.ua.isPhone) {
            router.beforePopState(() => {
                if (window.zE && window.zEActive) {
                    window.zEActive = false;
                    window.zE.hide();
                    return false;
                }
                return true;
            });
        }

        router.events.on('routeChangeStart', handleStart);
        router.events.on('routeChangeComplete', handleStop);
        router.events.on('routeChangeError', handleError);
        return () => {
            router.events.off('routeChangeStart', handleStart);
            router.events.off('routeChangeComplete', handleStop);
            router.events.off('routeChangeError', handleError);
        }
    }, [router]);

    useEffect(() => {
        // 获取当前用户信息
        userStore.fetchUserInfo().then(res => {
            const needLogin = (res.isGuestUser || !res.isLogin) && rootState.needAuth;
            if (needLogin) {
                if (rootState.ua.isApp) {
                    nativeLogin();
                } else {
                    // loginToken
                    router.replace(generatePath(RouteEnum.LOGIN, {
                        redirect: router.asPath || '/',
                        loginToken: void 0
                    }))
                }
            }

            if (rootState.ua.isApp) {
                miniCartStore.setTotalQty(res.cartNum || 0);
            }
        });
    }, []);

    const getLayout = Component.getLayout ?? (page => <BaseLayout {...rootState.layoutProps}>{page}</BaseLayout>);
    const StoreContext = getStoreContext();

    return (
        <>
            <GlobalMetas/>
            {theme.useRem && <RemInstaller/>}
            <GtmInstaller/>
            <GaInstaller/>
            <SnowplowInstaller/>
            {isPhone && ['test', 'pre'].includes(rootState.envName) && <VConsole/>}
            {!!site.aos && <AosInstaller/>}

            <I18nextProvider i18n={i18nextInstance}>
                <StoreContext.Provider value={observableStore}>
                    <RiskifiedBeacon/>
                    <PageSeo seoProps={seoProps} pageProps={pageProps}/>

                    {(pageProps.error || pageProps.errors || pageProps.errorTip)
                        ? getLayout(<ErrorDisplay {...pageProps}/>)
                        // @ts-ignore
                        : getLayout(<Component {...pageProps}/>)
                    }

                    <BtnDebug
                        pageProps={pageProps}
                        seoProps={seoProps}
                        storeProps={storeProps}
                    />

                    <GoogleConsent/>

                    {rootState.ua.isApp && rootState.envName !== 'prod' && <AppDebug/>}

                </StoreContext.Provider>
            </I18nextProvider>
        </>
    );
}

function MyApp(props: MyAppProps): ReactElement {
    const { asPath } = useRouter();
    // TODO: wholesale站，在已打开页面点击服务端渲染页面链接，触发 Login 302 跳转后（手动删除cookie 'LOGIN_TOKEN'），客户端与服务端执执行的 page 代码不一致导致报错
    if (!props?.storeProps) {
        console.log(!props ? 'MyApp props empty' : 'MyApp props.storeProps empty', asPath);
        // router.reload();
        return <></>;
    }
    return <MyAppMain {...props}/>
}

MyApp.getInitialProps = async (appContext: AppContext) => {
    const { ctx: { asPath, pathname, res } } = appContext;
    let parsed = parseAppContext(appContext);

    // extend pageConf
    const pageCode = parsed.pageCode;
    if (pageCode) {
        parsed = { ...pages[pageCode], ...parsed };
    }
    // layout
    parsed.pageLayout = getPageLayout(pathname);

    // store 初始化+更新 seo数据
    const [storeProps, seoProps] = await Promise.all([
        initLoadStore(parsed, appContext),
        parsed.seoCode ? getSeoConfig(parsed.seoCode, parsed.query) : null
    ]);

    const { userStore, serverStore } = getStore();

    // next页面与shopify 重点页面重定向，只有刷新或打开页面时生效
    if (typeof window === 'undefined') {
        const { uriMapList } = serverStore.state;
        if (uriMapList?.length) {
            const uriMapItem = uriMapList.find(item => item.baseUrl === (asPath as string).replace(/[?#].*$/, ''))
            if (uriMapItem) {
                if (res) {
                    res.writeHead(302, { Location: uriMapItem.linkUrl });
                    res.end();
                } else {
                    router.push(uriMapItem.linkUrl);
                }
            }
        }
    }

    if (!isPopupAageGateBrands && checkAgeGate(false, parsed.query, parsed.cookies)) {
        let redirectUrl = getAgeGatePath(asPath || '/')
        if (res) {
            res.writeHead(302, { Location: redirectUrl });
            res.end();
        } else {
            await router.replace(redirectUrl);
        }
    }

    if (pathname === RouteEnum.LOGIN) {
        // 已登录用户自动跳转到目标页面
        await userStore.fetchUserInfo({ from: 'loginPage' });
        if (userStore.state.isLogin) {
            const redirectUrl = (parsed.query.redirect as string) || '/';
            if (res) {
                res.writeHead(302, { Location: redirectUrl });
                res.end();
            } else {
                await router.replace(redirectUrl);
            }
        }
    } else if ((parsed.needAuth || (
        site.project === 'vs-ws'
        && !isStringIncludedArrayString(pathname, WHOLESALE_NO_LOGIN_PAGES)
        // && !WHOLESALE_NO_LOGIN_PAGES.includes(pathname as RouteEnum)
    )) && !userStore.checkAuth()) {

        // 鉴权
        let redirectUrl = generatePath(RouteEnum.LOGIN, { redirect: asPath || '/' })

        if (res) {
            res.writeHead(302, { Location: redirectUrl });
            res.end();
        } else {
            await router.push(redirectUrl);
        }
        return {};
    } else if (pathname === RouteEnum.CHECKOUT) {
        // checkout 返回
        const cartIdsInCookie = parsed.cookies[COOKIE_CART_SELECTED_IDS];
        const variantsInCookie = parsed.cookies[COOKIE_VARIANT_NUMS_ITEMS];
        const buyNowDataInCookie = parsed.cookies[COOKIE_BUY_NOW_DATA];
        const orderIdInCookie = parsed.cookies[COOKIE_LAST_ORDER_ID];

        if (!parsed.query.orderId && !cartIdsInCookie?.length && !variantsInCookie?.length && !buyNowDataInCookie && orderIdInCookie) {
            const reCheckoutUrl = generatePath(RouteEnum.CHECKOUT, { orderId: orderIdInCookie })
            if (res) {
                res.writeHead(302, { Location: reCheckoutUrl });
                res.end();
            } else {
                await router.replace(reCheckoutUrl);
            }
        }
    }

    const appProps = await App.getInitialProps(appContext);
    const { serverState, ...clientStoreProps } = storeProps;
    return {
        ...appProps,
        storeProps: {
            ...clientStoreProps,
            serverState: pathname === RouteEnum.CACHE ? serverState : void 0
        },
        seoProps,
    }
};

export default MyApp;
